import { useState, useEffect, useCallback, useRef } from 'react';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import CreateHomeNudge from '../components/CreateHomeNudge';
import { Plus, Pencil, Check, Trash2, X, Loader2, AlertCircle, ShoppingCart, Share2, ChevronDown, RefreshCw } from 'lucide-react';
import { UnitPrompt } from '../components/HomeStateBanners';
import { homeApi } from '../services/homeApi';
import { pageErrorFromApi } from '../services/planLimitModal';
import QuantityStepper from '../components/QuantityStepper';
import { useAuth } from '../contexts/AuthContext';
// FEATURE (2026-10-08, Jaime): per-list + button uses i18n for the aria-label.
import { useTranslation } from 'react-i18next';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { useEntitlements } from '../hooks/useEntitlements';
import { homeModuleEntitled } from '../utils/homeEntitlementGate';
// Beacon lane (2026-10-01): silent empty-200 diagnostic (TH1 class).
import { beaconEmptySuccess } from '../lib/homeBeacons';

interface ShoppingItem {
  id: string;
  name: string;
  quantity: string;
  category: string;
  assignedTo: string;
  urgency: 'low' | 'medium' | 'high';
  purchased: boolean;
  status: 'pending' | 'completed';
}

// FEATURE (2026-08-31, Jaime): product categories are now exactly these four.
// Legacy food-style ids (fruits/dairy/meat/pantry/cleaning/other/general) are
// mapped to the closest new category for DISPLAY only — no DB data is rewritten.
const categories = [
  { id: 'mercado', name: 'Mercado', icon: '🛒' },
  { id: 'limpieza', name: 'Limpieza', icon: '🧼' },
  { id: 'mantenimiento', name: 'Mantenimiento', icon: '🔧' },
  { id: 'otros', name: 'Otros', icon: '📦' },
];

const CATEGORY_IDS = new Set(categories.map(c => c.id));
const LEGACY_CATEGORY_MAP: Record<string, string> = {
  fruits: 'mercado',
  dairy: 'mercado',
  meat: 'mercado',
  pantry: 'mercado',
  cleaning: 'limpieza',
  maintenance: 'mantenimiento',
  other: 'otros',
  general: 'otros',
};
// FEATURE (2026-09-23, Jaime): list items render alphabetically A-Z within each
// group (category sections and the purchased section). Case-insensitive,
// Spanish-locale sort so accents order naturally (e.g. Áloe after Abrecereales).
const compareByName = (a: { name: string }, b: { name: string }): number =>
  a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });

const normalizeCategory = (raw?: string | null): string => {
  const key = (raw ?? '').trim().toLowerCase();
  if (LEGACY_CATEGORY_MAP[key]) return LEGACY_CATEGORY_MAP[key];
  return key && CATEGORY_IDS.has(key) ? key : 'otros';
};

const urgencyColors: Record<string, string> = {
  low: 'text-gray-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-600 dark:text-red-400',
};

const urgencyLabels: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
};

const emptyForm = {
  name: '',
  quantity: '1',
  category: 'otros',
  assignedTo: '',
  urgency: 'medium' as ShoppingItem['urgency'],
};

export default function HomeShopping() {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  // FIX (2026-08-31, Jaime bug): explicit active-unit scoping for every
  // home-module request (owned unit OR condo membership unit, e.g. TH1).
  const activeUnitId = useActiveHomeUnitId();
  // fix-173c: entitlement gate — usuariosOnly / home-* stripped payload means
  // NO /api/home/shopping call at all (no 403 banner; neutral empty state).
  const { entitlement, loading: entitlementLoading } = useEntitlements();
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // FEATURE (2026-08-31, Jaime): inline item edit + row quantity stepper.
  // PUT /api/home/shopping/:id accepts itemName/quantity/category/assignedTo/
  // urgency/status (DAL allowlist in database/home.js Shopping.update).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', quantity: '1', category: 'otros', urgency: 'medium' as ShoppingItem['urgency'] });
  const [editSaving, setEditSaving] = useState(false);

  // FEATURE (2026-08-31): "don't retype products" typeahead
  interface Suggestion {
    itemName?: string;
    item_name?: string;
    name?: string;
    category?: string;
    lastQuantity?: string | number;
    last_quantity?: string | number;
    lastUrgency?: string;
    last_urgency?: string;
    timesAdded?: number;
    times_added?: number;
  }
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // FEATURE (2026-08-31, Jaime): "Compartir" is now export-as-text. The
  // pending items are formatted as "Producto x CANT" lines and handed to the
  // OS share sheet (navigator.share → WhatsApp/SMS/email on mobile), with a
  // clipboard fallback + toast on desktop.
  const [shareToast, setShareToast] = useState<string | null>(null);

  // FEATURE (2026-10-08, Jaime): share with list selection. Compartir opens a
  // small multi-select picker (Mercado/Mantenimiento/Limpieza/Otros); only the
  // pending items of the selected lists go into the share text. Same share
  // mechanism as before (navigator.share → clipboard fallback).
  const [showSharePicker, setShowSharePicker] = useState(false);
  const [shareSelected, setShareSelected] = useState<string[]>(categories.map(c => c.id));
  const [shareError, setShareError] = useState<string | null>(null);
  const shareCounts: Record<string, number> = {};
  categories.forEach(c => {
    shareCounts[c.id] = items.filter(i => i.category === c.id && !i.purchased).length;
  });
  // FIX (2026-08-31): backend rows are snake_case (item_name, assigned_to,
  // status, purchased_at). Normalize once so the UI always has name/purchased.
  const normalizeItem = (raw: any): ShoppingItem => ({
    id: raw.id,
    name: raw.itemName ?? raw.item_name ?? raw.name ?? '',
    quantity: raw.quantity ?? '',
    category: normalizeCategory(raw.category),
    assignedTo: raw.assignedTo ?? raw.assigned_to ?? '',
    urgency: (raw.urgency ?? 'medium') as ShoppingItem['urgency'],
    purchased: raw.status === 'completed' || raw.purchased === true || !!raw.purchased_at,
    status: raw.status ?? (raw.purchased || raw.purchased_at ? 'completed' : 'pending'),
  });

  const fetchItems = useCallback(async () => {
    // Jaime 2026-09-30: never fire a unitId-dependent request without a
    // resolved unit — show the unit-picker prompt state instead.
    if (!activeUnitId) { setItems([]); setError(null); setLoading(false); return; }
    // fix-1004 (BUG-A, QA bugfix-cycle-1004-0400): entitlement fail-open edge.
    // The 173c gate must only skip the fetch when a payload EXISTS and denies
    // home-shopping. While the payload is still loading we wait (usuariosOnly
    // must not hit /api/home/*); if it never arrives (fetch error / no cached
    // value) we fail OPEN per homeEntitlementGate.ts's documented rule — the
    // backend remains the authoritative 403 guard, so behavior degrades to
    // pre-173c, never worse. The previous `!entitlementReady` check failed
    // closed forever whenever the payload was unavailable, so entitled users
    // never fetched the list (no GET on load or after a 201 create).
    if (!entitlement && entitlementLoading) { return; }
    if (entitlement && !homeModuleEntitled(entitlement, 'home-shopping')) { setItems([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await homeApi.shopping.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const items = payload?.items || payload?.data?.items || (Array.isArray(payload) ? payload : []);
      const fetched = Array.isArray(items) ? items.map(normalizeItem) : [];
      // Beacon: 200 with an empty list → session-deduped empty_success report.
      beaconEmptySuccess({ route: '/shopping', unitId: activeUnitId, rowCount: fetched.length });
      // Keep local state for rows inside the 3s checked-feedback window so a
      // refetch can't yank them into "Comprado" early.
      setItems(prev => {
        const localById = new Map(prev.map(i => [i.id, i] as const));
        return fetched.map(f => {
          const local = localById.get(f.id);
          return local && justCheckedRef.current.has(f.id) ? local : f;
        });
      });
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al cargar lista de compras'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeUnitId, entitlement, entitlementLoading]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Ids with an in-flight optimistic mutation (status toggle / qty stepper).
  // The poller must not clobber these rows mid-update (same reconcile idea as
  // togglePurchased: never revert an optimistic local state).
  const mutatingIds = useRef<Set<string>>(new Set());

  // FEATURE (2026-09-18, Jaime): when a product is tapped as bought, it shows
  // CHECKED immediately but stays in the pending list for ~3s (visual
  // feedback) before moving to "Comprado". The API call fires immediately;
  // only the LIST TRANSITION is delayed. Unchecking during the 3s window
  // cancels the pending move and reverts/persists status back to 'pending'.
  const [justChecked, setJustChecked] = useState<Set<string>>(new Set());
  const justCheckedRef = useRef<Set<string>>(new Set());
  const moveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const addJustChecked = (id: string) => {
    justCheckedRef.current.add(id);
    setJustChecked(new Set(justCheckedRef.current));
  };
  const removeJustChecked = (id: string) => {
    if (!justCheckedRef.current.has(id)) return;
    justCheckedRef.current.delete(id);
    setJustChecked(new Set(justCheckedRef.current));
  };

  // Unmount cleanup: no stray timers firing after the screen is gone.
  useEffect(() => () => {
    moveTimersRef.current.forEach(t => clearTimeout(t));
    moveTimersRef.current.clear();
  }, []);

  // POLLING v1 (2026-08-31, Jaime): pragmatic ~6s refresh while this screen is
  // mounted so items added/edited/completed by other users of the same unit
  // appear without a manual reload. NOTE: this is polling v1 — a websocket
  // layer can replace it later without changing the merge semantics below.
  // Rules:
  //   - tick only when document.visibilityState === 'visible' (paused on
  //     hidden tabs; an immediate catch-up poll fires on visibilitychange)
  //   - skipped while an inline edit / add form is open so we
  //     never clobber someone mid-edit
  //   - rows with an in-flight optimistic mutation keep their local values
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (editingId || showForm) return;
      if (mutatingIds.current.size > 0) return;
      // fix-173c: entitlement gate — no poll when home-shopping is not entitled.
      if (!entitlement || !homeModuleEntitled(entitlement, 'home-shopping')) return;
      try {
        const res = await homeApi.shopping.list(activeUnitId);
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const list = payload?.items || payload?.data?.items || (Array.isArray(payload) ? payload : []);
        if (!Array.isArray(list)) return;
        const fetched = list.map(normalizeItem);
        setItems(prev => {
          const localById = new Map(prev.map(i => [i.id, i] as const));
          return fetched.map(f => {
            const local = localById.get(f.id);
            return local && mutatingIds.current.has(f.id) ? local : f;
          });
        });
        setError(null);
      } catch {
        // Silent: a transient poll error must not replace the visible list.
      }
    };
    const t = setInterval(poll, 6000);
    const onVis = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // FIX (2026-08-31, Jaime bug): include activeUnitId in deps. Without it the
    // interval captured the unitId from the first mount, so after switching
    // units the ~6s poll kept refetching the PREVIOUS unit's list and
    // overwriting the (correct) unit-scoped fetch. Re-creating the interval on
    // unitId change gives every tick a fresh, explicit unitId.
  }, [editingId, showForm, activeUnitId, entitlement]);

  // Debounced suggestion fetch (>=1 char, hide when no matches)
  useEffect(() => {
    const q = form.name.trim();
    if (!q) { setSuggestions([]); setShowSuggestions(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      // fix-173c: no suggestion API call when home-shopping is not entitled.
      if (!entitlement || !homeModuleEntitled(entitlement, 'home-shopping')) return;
      try {
        // Explicit unitId: requireUnit auto-resolve breaks multi-unit owners
        const res = await homeApi.shopping.suggestions(q, activeUnitId);
        const payload = res.data?.data ?? res.data;
        const list = payload?.suggestions ?? payload?.items ?? (Array.isArray(payload) ? payload : []);
        const norm = (Array.isArray(list) ? list : []).map((sg: any) => ({
          itemName: sg.itemName ?? sg.item_name ?? sg.name ?? '',
          category: normalizeCategory(sg.category),
          lastQuantity: sg.lastQuantity ?? sg.last_quantity ?? undefined,
          lastUrgency: sg.lastUrgency ?? sg.last_urgency ?? undefined,
          timesAdded: sg.timesAdded ?? sg.times_added ?? 0,
        })).filter((sg: any) => sg.itemName);
        if (cancelled) return;
        setSuggestions(norm);
        setShowSuggestions(norm.length > 0);
      } catch {
        if (!cancelled) { setSuggestions([]); setShowSuggestions(false); }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.name, activeUnitId, entitlement]);

  const applySuggestion = (sg: Suggestion) => {
    setForm(prev => ({
      ...prev,
      name: sg.itemName ?? '',
      category: sg.category ?? prev.category,
      quantity: sg.lastQuantity != null ? String(sg.lastQuantity) : prev.quantity,
      urgency: (sg.lastUrgency as ShoppingItem['urgency']) ?? prev.urgency,
    }));
    setShowSuggestions(false);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      await homeApi.shopping.create({ ...form, itemName: form.name }, activeUnitId);
      setShowForm(false);
      setForm(emptyForm);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al agregar item'));
    }
  };

  // FIX (2026-08-31): status contract is 'pending' | 'completed'. Optimistic
  // update on the status field, then reconcile from the response item; revert
  // on error so the check circle never silently reverts.
  const togglePurchased = async (item: ShoppingItem) => {
    // Cancel any pending 3s move for this row (single timer per id).
    const pendingTimer = moveTimersRef.current.get(item.id);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      moveTimersRef.current.delete(item.id);
    }

    // Uncheck DURING the 3s feedback window: cancel the move, revert locally
    // and persist status back to 'pending' on the server.
    if (justCheckedRef.current.has(item.id)) {
      removeJustChecked(item.id);
      mutatingIds.current.add(item.id);
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, status: 'pending', purchased: false } : i)));
      try {
        await homeApi.shopping.update(item.id, { status: 'pending' }, activeUnitId);
      } catch (err: any) {
        fetchItems();
        setError(pageErrorFromApi(err, 'Error al actualizar item'));
      } finally {
        mutatingIds.current.delete(item.id);
      }
      return;
    }

    // Plain uncheck from the "Comprado" list: unchanged behavior.
    if (item.purchased) {
      mutatingIds.current.add(item.id);
      setItems(prev => prev.map(i => (i.id === item.id
        ? { ...i, status: 'pending', purchased: false }
        : i)));
      try {
        const res = await homeApi.shopping.update(item.id, { status: 'pending' }, activeUnitId);
        const updated = res.data?.data?.item ?? res.data?.item;
        if (updated) {
          const normalized = normalizeItem(updated);
          setItems(prev => prev.map(i => (i.id === item.id ? normalized : i)));
        } else {
          fetchItems();
        }
      } catch (err: any) {
        // Revert optimistic change
        setItems(prev => prev.map(i => (i.id === item.id ? item : i)));
        setError(pageErrorFromApi(err, 'Error al actualizar item'));
      } finally {
        mutatingIds.current.delete(item.id);
      }
      return;
    }

    // Check: mark CHECKED visually right away, fire the API immediately, and
    // only move the row to "Comprado" after ~3s. While the row is in the
    // feedback window it keeps purchased=false so the pending list/computed
    // counts are unaffected; the server is already 'completed'.
    mutatingIds.current.add(item.id);
    addJustChecked(item.id);
    try {
      await homeApi.shopping.update(item.id, { status: 'completed' }, activeUnitId);
    } catch (err: any) {
      removeJustChecked(item.id);
      mutatingIds.current.delete(item.id);
      setError(pageErrorFromApi(err, 'Error al actualizar item'));
      return;
    }
    const t = setTimeout(() => {
      moveTimersRef.current.delete(item.id);
      mutatingIds.current.delete(item.id);
      removeJustChecked(item.id);
      setItems(prev => prev.map(i => (i.id === item.id
        ? { ...i, status: 'completed', purchased: true }
        : i)));
    }, 3000);
    moveTimersRef.current.set(item.id, t);
  };

  // FEATURE (2026-08-31, Jaime): open inline edit with the item's current values.
  const openEdit = (item: ShoppingItem) => {
    setEditingId(item.id);
    setEditForm({
      name: item.name,
      quantity: item.quantity || '1',
      category: item.category,
      urgency: item.urgency,
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.name.trim()) return;
    setEditSaving(true);
    try {
      await homeApi.shopping.update(editingId, {
        itemName: editForm.name.trim(),
        quantity: editForm.quantity,
        category: editForm.category,
        urgency: editForm.urgency,
      }, activeUnitId);
      setEditingId(null);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al actualizar item'));
    } finally {
      setEditSaving(false);
    }
  };

  // FEATURE (2026-08-31, Jaime): row +/- stepper. Optimistic quantity update,
  // then reconcile the quantity from the response item — same pattern as
  // togglePurchased. Minus is disabled at quantity 1 (items never auto-delete
  // at 0; deletion stays an explicit action).
  const changeQty = async (item: ShoppingItem, delta: number) => {
    const current = parseInt(item.quantity, 10);
    const base = Number.isFinite(current) && current > 0 ? current : 1;
    const next = Math.max(1, base + delta);
    if (next === base) return;
    mutatingIds.current.add(item.id);
    setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: String(next) } : i)));
    try {
      const res = await homeApi.shopping.update(item.id, { quantity: String(next) }, activeUnitId);
      const updated = res.data?.data?.item ?? res.data?.item;
      if (updated) {
        const normalized = normalizeItem(updated);
        setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: normalized.quantity } : i)));
      }
    } catch (err: any) {
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: item.quantity } : i)));
      setError(pageErrorFromApi(err, 'Error al actualizar cantidad'));
    } finally {
      mutatingIds.current.delete(item.id);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // FIX (2026-09-28, ticket #216): pass the active unitId — same source as
      // the GET calls — otherwise requireUnit 400s "unitId is required" for
      // multi-unit owners and the item never gets removed.
      await homeApi.shopping.delete(id, activeUnitId);
      fetchItems();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al eliminar item'));
    }
  };

  const shareList = async () => {
    // FEATURE (2026-10-08, Jaime): share only the selected lists' pending items.
    const pending = items.filter(i => !i.purchased && shareSelected.includes(i.category));
    if (pending.length === 0) {
      setShareToast(t('homeShopping.shareEmpty'));
      setTimeout(() => setShareToast(null), 2500);
      return;
    }
    const text = pending
      .map(i => `${i.name} x ${parseInt(i.quantity, 10) || 1}`)
      .join('\n');
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: t('homeShopping.shareTitle'), text });
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setShareToast(t('homeShopping.shareCopied'));
    } catch {
      setShareToast(t('homeShopping.shareCopyFail'));
    }
    setTimeout(() => setShareToast(null), 3000);
  };
  const openSharePicker = () => {
    // FEATURE (2026-10-08, Jaime): preselect lists that actually have pending items.
    const listsWithPending = categories.filter(c => items.some(i => i.category === c.id && !i.purchased));
    setShareSelected(listsWithPending.map(c => c.id));
    setShareError(null);
    setShowSharePicker(true);
  };

  const toggleShareList = (id: string) =>
    setShareSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const cancelShare = () => {
    setShowSharePicker(false);
    setShareError(null);
  };
  const confirmShare = async () => {
    if (shareSelected.length === 0) {
      setShareError(t('homeShopping.shareRequired'));
      return;
    }
    setShowSharePicker(false);
    setShareError(null);
    await shareList();
  };

  // FEATURE (2026-10-08, Jaime): open the existing add-product form with a
  // specific list (category) pre-selected. Triggered by the small "+" button
  // beside each list title (Mercado/Mantenimiento/Limpieza/Otros). The generic
  // "Agregar Item" button keeps opening the form with the default category.
  const openAddFor = (categoryId: string) => {
    setForm({ ...emptyForm, category: categoryId });
    setSuggestions([]);
    setShowSuggestions(false);
    setShowForm(true);
    if (typeof document !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const uncheckedCount = items.filter(i => !i.purchased).length;
  const checkedCount = items.filter(i => i.purchased).length;

  // FEATURE (2026-09-03, Jaime): collapsible category sections. Default is
  // expanded (no behavior change on load); each category toggles independently.
  // Collapsible-section state is persisted in localStorage under a single key
  // so collapse choices survive reloads. Guarded parse falls back to defaults.
  const COLLAPSED_SECTIONS_KEY = 'home-shopping-collapsed-sections';
  const loadCollapsedSections = (): { categories: Record<string, boolean>; purchased: boolean } => {
    if (typeof window === 'undefined') return { categories: {}, purchased: false };
    try {
      const raw = window.localStorage.getItem(COLLAPSED_SECTIONS_KEY);
      if (!raw) return { categories: {}, purchased: false };
      const parsed = JSON.parse(raw);
      return {
        categories: parsed && typeof parsed === 'object' && typeof parsed.categories === 'object' && parsed.categories !== null ? parsed.categories : {},
        purchased: parsed?.purchased === true,
      };
    } catch {
      return { categories: {}, purchased: false };
    }
  };
  const writeCollapsedSections = (next: { categories: Record<string, boolean>; purchased: boolean }) => {
    try {
      window.localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
    } catch {
      // storage unavailable (quota/private mode) — collapse state just won't persist
    }
  };
  const initialCollapsed = loadCollapsedSections();
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>(initialCollapsed.categories);
  const [purchasedCollapsed, setPurchasedCollapsed] = useState(initialCollapsed.purchased);
  const toggleCategoryCollapsed = (id: string) =>
    setCollapsedCategories(prev => {
      const next = { ...prev, [id]: !prev[id] };
      writeCollapsedSections({ categories: next, purchased: purchasedCollapsed });
      return next;
    });

  const itemsByCategory = categories.map(cat => ({
    ...cat,
    items: items.filter(i => i.category === cat.id && !i.purchased).sort(compareByName),
  }));

  return (
    <div className="space-y-6">
      <CreateHomeNudge />
      {/* Header Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">Por comprar</p>
            <p className="text-4xl font-bold text-white mt-1">{uncheckedCount}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Comprado</p>
            <p className="text-2xl font-bold text-white mt-1">{checkedCount}</p>
          </div>
        </div>
      </div>

      {!activeUnitId && !loading && <UnitPrompt />}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => { setError(null); void fetchItems(); }} className="ml-auto flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Add + Share Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => { setForm(emptyForm); setSuggestions([]); setShowSuggestions(false); setShowForm(true); }}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Agregar Item
        </button>
        <button
          onClick={openSharePicker}
          aria-haspopup="dialog"
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        >
          <Share2 className="w-5 h-5" />
          Compartir
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">Nuevo Item</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={e => { if (e.key === 'Escape') setShowSuggestions(false); }}
              placeholder="¿Qué necesitas comprar?"
              className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
              autoComplete="off"
            />
            {showForm && showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg overflow-hidden">
                {suggestions.map((sg, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); applySuggestion(sg); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-900 dark:text-white text-sm"
                  >
                    <span>{categories.find(c => c.id === (sg.category ?? 'other'))?.icon ?? '📦'}</span>
                    <span className="flex-1 truncate">{sg.itemName}</span>
                    {!!sg.lastQuantity && <span className="text-gray-500 dark:text-slate-400 text-xs">×{sg.lastQuantity}</span>}
                    {!!sg.timesAdded && <span className="text-gray-400 dark:text-slate-500 text-xs">({sg.timesAdded}×)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* FEATURE (2026-08-31, Jaime): shared +/- stepper in create form */}
            <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg border border-gray-300 dark:border-slate-600 px-2">
              <QuantityStepper
                value={parseInt(form.quantity, 10) || 1}
                onChange={n => setForm({ ...form, quantity: String(n) })}
              />
            </div>
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={form.assignedTo}
              onChange={e => setForm({ ...form, assignedTo: e.target.value })}
              placeholder="Asignar a..."
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <select
              value={form.urgency}
              onChange={e => setForm({ ...form, urgency: e.target.value as ShoppingItem['urgency'] })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="low">Urgencia Baja</option>
              <option value="medium">Urgencia Media</option>
              <option value="high">Urgencia Alta</option>
            </select>
          </div>
          <button
            onClick={handleSubmit}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            Agregar a la Lista
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando lista...
        </div>
      )}

      {/* Items by Category */}
      {!loading && !error && activeUnitId && (
        <div className="space-y-6">
          {itemsByCategory.length === 0 && checkedCount === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-slate-400">La lista está vacía</p>
              <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">¡Agrega items para comprar!</p>
              <button
                type="button"
                onClick={() => { setForm(emptyForm); setSuggestions([]); setShowSuggestions(false); setShowForm(true); }}
                className="mt-4 rounded-xl bg-[#36CCD8] px-4 py-2.5 text-sm font-semibold text-white"
              >
                Agregar el primer artículo
              </button>
            </div>
          ) : (
            itemsByCategory.map((cat) => (
              <div key={cat.id} className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
                {/* FEATURE (2026-10-08, Jaime): header is now a container with
                    the collapse toggle + a per-list "+" button that opens the
                    add-product form with THIS list pre-selected. */}
                <div className="w-full bg-gray-100 dark:bg-slate-700/50 px-4 py-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleCategoryCollapsed(cat.id)}
                    aria-expanded={!collapsedCategories[cat.id]}
                    aria-controls={`shopping-cat-${cat.id}`}
                    className="flex-1 min-w-0 hover:bg-gray-200 dark:hover:bg-slate-700 -my-1 py-1 flex items-center gap-2 text-left transition-colors rounded"
                  >
                    <span className="text-xl">{cat.icon}</span>
                    <span className="font-semibold text-[var(--v-text-primary)] dark:text-white">{cat.name}</span>
                    <span className="text-gray-500 dark:text-slate-400 text-sm ml-auto">{cat.items.length}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 dark:text-slate-400 flex-shrink-0 transition-transform ${collapsedCategories[cat.id] ? '-rotate-90' : ''}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => openAddFor(cat.id)}
                    aria-label={t('homeShopping.addToList', { list: cat.name })}
                    title={t('homeShopping.addToList', { list: cat.name })}
                    data-testid={`shopping-add-${cat.id}`}
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div id={`shopping-cat-${cat.id}`} className={collapsedCategories[cat.id] ? 'hidden' : 'p-4 space-y-2'}>
                  {cat.items.length === 0 && (
                    <p className="text-gray-400 dark:text-slate-500 text-sm">{t('homeShopping.sectionEmpty')}</p>
                  )}
                  {cat.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-200 dark:hover:bg-slate-700/50 rounded-lg group"
                    >
                      <button
                        onClick={() => togglePurchased(item)}
                        aria-label={justChecked.has(item.id) ? 'Marcado como comprado' : 'Marcar como comprado'}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${justChecked.has(item.id) ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-slate-500 hover:border-green-500'}`}
                      >
                        {justChecked.has(item.id) && <Check className="w-4 h-4 text-white" />}
                      </button>
                      {editingId === item.id ? (
                        /* FEATURE (2026-08-31, Jaime): inline edit form */
                        <div className="flex-1 min-w-0 grid grid-cols-2 gap-2 py-1">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="Nombre"
                            className="col-span-2 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                            autoComplete="off"
                          />
                          {/* FEATURE (2026-08-31, Jaime): shared +/- stepper in edit form */}
                          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg border border-gray-300 dark:border-slate-600 px-1.5">
                            <QuantityStepper
                              size="sm"
                              value={parseInt(editForm.quantity, 10) || 1}
                              onChange={n => setEditForm({ ...editForm, quantity: String(n) })}
                            />
                          </div>
                          <select
                            value={editForm.category}
                            onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                            className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                          >
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                          <select
                            value={editForm.urgency}
                            onChange={e => setEditForm({ ...editForm, urgency: e.target.value as ShoppingItem['urgency'] })}
                            className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                          >
                            <option value="low">Urgencia Baja</option>
                            <option value="medium">Urgencia Media</option>
                            <option value="high">Urgencia Alta</option>
                          </select>
                          <div className="flex gap-2">
                            <button
                              onClick={saveEdit}
                              disabled={editSaving || !editForm.name.trim()}
                              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-600/40 text-white py-1.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
                            >
                              {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-1.5 px-3 rounded-lg text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <p className="text-[var(--v-text-primary)] dark:text-white font-medium">{item.name}</p>
                            <p className="text-gray-500 dark:text-slate-400 text-sm">
                              {item.quantity}
                              {item.assignedTo && ` • ${item.assignedTo}`}
                              {item.urgency && (
                                <span className={`ml-2 ${urgencyColors[item.urgency]}`}>⚠ {urgencyLabels[item.urgency]}</span>
                              )}
                            </p>
                          </div>
                          {/* FEATURE (2026-08-31, Jaime): +/- quantity stepper (shared component) */}
                          <QuantityStepper
                            size="sm"
                            value={parseInt(item.quantity, 10) || 1}
                            onChange={n => changeQty(item, n - (parseInt(item.quantity, 10) || 1))}
                          />
                          <button
                            onClick={() => openEdit(item)}
                            className="text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto transition-opacity"
                            aria-label="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Purchased Items */}
      {!loading && checkedCount > 0 && (
        <div className="bg-[var(--v-surface-1)]/50 rounded-xl border border-[var(--v-surface-3)] p-4">
          <button
            type="button"
            onClick={() =>
              setPurchasedCollapsed(v => {
                const next = !v;
                writeCollapsedSections({ categories: collapsedCategories, purchased: next });
                return next;
              })
            }
            aria-expanded={!purchasedCollapsed}
            aria-controls="shopping-purchased"
            className="w-full text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 font-medium flex items-center gap-2 mb-4 text-left"
          >
            <Check className="w-4 h-4" />
            Comprado ({checkedCount})
            <ChevronDown
              className={`w-4 h-4 ml-auto flex-shrink-0 transition-transform ${purchasedCollapsed ? '-rotate-90' : ''}`}
            />
          </button>
          <div id="shopping-purchased" className={purchasedCollapsed ? 'hidden' : 'space-y-2 opacity-60'}>
            {items.filter(i => i.purchased).sort(compareByName).map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-2 group">
                <button
                  onClick={() => togglePurchased(item)}
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"
                >
                  <Check className="w-4 h-4 text-white" />
                </button>
                <span className="text-gray-500 dark:text-slate-400 line-through flex-1">{item.name}</span>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 p-1 flex-shrink-0 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FEATURE (2026-10-08, Jaime): share list-selection modal */}
      {showSharePicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('homeShopping.shareTitle')}
          onClick={cancelShare}
        >
          <div
            className="bg-[var(--v-surface-1)] dark:bg-slate-800 w-full max-w-sm rounded-xl border border-[var(--v-surface-3)] p-4 space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">{t('homeShopping.shareTitle')}</h3>
              <button onClick={cancelShare} aria-label={t('homeShopping.shareCancel')} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-500 dark:text-slate-400 text-sm">{t('homeShopping.shareHint')}</p>
            <div className="space-y-2">
              {categories.map(cat => {
                const checked = shareSelected.includes(cat.id);
                const count = shareCounts[cat.id] || 0;
                return (
                  <label
                    key={cat.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-purple-500 bg-purple-500/10' : 'border-gray-300 dark:border-slate-600 hover:border-purple-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleShareList(cat.id)}
                      aria-label={t('homeShopping.shareAria', { list: cat.name })}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <span aria-hidden="true">{cat.icon}</span>
                    <span className="text-[var(--v-text-primary)] dark:text-white font-medium flex-1">{cat.name}</span>
                    <span className="text-gray-500 dark:text-slate-400 text-sm">
                      {count > 0 ? t('homeShopping.sharePendingCount', { count }) : '—'}
                    </span>
                  </label>
                );
              })}
            </div>
            {shareError && (
              <p className="text-red-600 dark:text-red-400 text-sm" role="alert">{shareError}</p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={confirmShare}
                disabled={shareSelected.length === 0}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/40 text-white py-2.5 rounded-lg font-medium"
              >
                {t('homeShopping.shareAction')}
              </button>
              <button
                onClick={cancelShare}
                className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 py-2.5 px-4 rounded-lg text-sm"
              >
                {t('homeShopping.shareCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FEATURE (2026-08-31, Jaime): clipboard fallback toast */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm z-50">
          {shareToast}
        </div>
      )}
    </div>
  );
}
